/**
 * MOCKUP — Tutte le schermate aggiuntive di HostFix.
 * Look "Enterprise Slate": charcoal + cobalt + emerald. Software pro, niente luxury.
 */
import React from 'react';
import {
  Building2, MapPin, ChevronRight, ArrowUpRight, Wrench, UserCog, Users,
  Calendar, FileText, Search, Plus, Filter, MoreHorizontal, Bell, Settings,
  AlertOctagon, AlertTriangle, CheckCircle2, Clock, Activity, BarChart3,
  Droplets, Zap, TrendingUp, Camera, Shield, Crown, X, Edit3, Trash2,
  Mail, Phone, ChevronDown, Sparkles, ArrowDown
} from 'lucide-react';
import { C, fontHead, overline, primaryBtn, secondaryBtn } from '../_tokens';

// ============================================================================
// Common helpers
// ============================================================================
const PageHeader = ({ over, title, sub, action }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
    <div>
      <div style={{ ...overline, marginBottom: 6 }}>{over}</div>
      <h1 style={{ fontFamily: fontHead, fontSize: 34, fontWeight: 600, margin: 0,
                   letterSpacing: '-0.025em', lineHeight: 1 }}>{title}</h1>
      {sub && <div style={{ color: C.textSec, fontSize: 13, marginTop: 8 }}>{sub}</div>}
    </div>
    {action}
  </div>
);

const Card = ({ title, sub, action, children, accent }) => (
  <div style={{
    background: C.surf, borderRadius: 14, padding: 22,
    border: `1px solid ${accent ? accent + '33' : C.border}`,
  }}>
    {(title || action) && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          {title && <div style={{ fontFamily: fontHead, fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</div>}
          {sub && <div style={{ fontSize: 11.5, color: C.textSec, marginTop: 3 }}>{sub}</div>}
        </div>
        {action}
      </div>
    )}
    {children}
  </div>
);

const Wrap = ({ children }) => (
  <div style={{ padding: '32px 36px', background: C.bg, color: C.text, fontFamily: 'Inter', minHeight: '100%' }}>
    {children}
  </div>
);

// ============================================================================
// 1. HOTEL MANAGER DASHBOARD
// ============================================================================
export function ScreenHotelManager() {
  return (
    <Wrap>
      <PageHeader
        over="Hotel Manager · Galleria"
        title="Dashboard Hotel"
        sub="Operations overview · oggi · 96 camere · occupancy 84.5%"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...secondaryBtn }}><Calendar size={13} /> Giugno 2026</button>
            <button style={{ ...primaryBtn }}>Report mensile <ArrowUpRight size={13} /></button>
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
        <Mini label="Ticket aperti" value="12" sub="3 urgenti" accent={C.terra} />
        <Mini label="Manutenzioni mese" value="8" sub="2 in scadenza" accent={C.gold} />
        <Mini label="Indice efficienza" value="78.4" sub="+2.3 vs Apr" accent={C.emerald} />
        <Mini label="Alert consumi" value="1" sub="acqua +28%" accent={C.amber} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14, marginBottom: 18 }}>
        <Card title="Heatmap problemi camere" sub="Ultimi 30 giorni · concentrazione per piano">
          <FloorHeatmap />
        </Card>
        <Card title="Team operativo" sub="Online ora · 5 tecnici">
          <TeamList />
        </Card>
      </div>

      <Card title="Attività recente" sub="Live · ticket + manutenzioni" action={
        <button style={{ background: 'transparent', border: 'none', color: C.gold, fontSize: 12, cursor: 'pointer' }}>Vedi tutto →</button>
      }>
        <ActivityFeed />
      </Card>
    </Wrap>
  );
}

const Mini = ({ label, value, sub, accent }) => (
  <div style={{ background: C.surf, borderRadius: 12, padding: 18, border: `1px solid ${C.border}` }}>
    <div style={{ ...overline, marginBottom: 8 }}>{label}</div>
    <div style={{ fontFamily: fontHead, fontSize: 32, fontWeight: 600, color: accent, letterSpacing: '-0.03em', lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 11, color: C.textSec, marginTop: 6 }}>{sub}</div>
  </div>
);

const FloorHeatmap = () => {
  const floors = [5, 4, 3, 2, 1];
  return (
    <div style={{ display: 'grid', gap: 5 }}>
      {floors.map(f => (
        <div key={f} style={{ display: 'grid', gridTemplateColumns: '40px 1fr', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: C.textSec, fontWeight: 600 }}>P. {f}</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(20, 1fr)', gap: 3 }}>
            {Array.from({ length: 20 }).map((_, i) => {
              const v = Math.random();
              const bg = v < 0.3 ? `${C.emerald}30` :
                         v < 0.6 ? `${C.gold}30` :
                         v < 0.85 ? `${C.amber}40` : `${C.terra}50`;
              return <div key={i} style={{ aspectRatio: '1', borderRadius: 3, background: bg,
                                             border: `1px solid ${C.border}` }} />;
            })}
          </div>
        </div>
      ))}
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', gap: 14, fontSize: 11, color: C.textSec }}>
        <Legend c={`${C.emerald}30`} l="0 ticket" />
        <Legend c={`${C.gold}30`} l="1-2" />
        <Legend c={`${C.amber}40`} l="3-5" />
        <Legend c={`${C.terra}50`} l="6+" />
      </div>
    </div>
  );
};

const Legend = ({ c, l }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
    <span style={{ width: 11, height: 11, background: c, borderRadius: 3, border: `1px solid ${C.border}` }} />
    {l}
  </span>
);

const TeamList = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {[
      { n: 'Luca Marini', r: 'Idraulico', s: 'online', t: 2, c: C.cyan },
      { n: 'Marco Trento', r: 'Elettricista', s: 'online', t: 1, c: C.amber },
      { n: 'Sara Konti', r: 'Multi-skill', s: 'online', t: 3, c: C.emerald },
      { n: 'Diego Bo', r: 'HVAC', s: 'pausa', t: 0, c: C.gold },
      { n: 'Anna Verde', r: 'Housekeeping', s: 'online', t: 1, c: '#A78BFA' },
    ].map(t => (
      <div key={t.n} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                              background: 'rgba(255,255,255,0.015)', borderRadius: 9, border: `1px solid ${C.border}` }}>
        <div style={{ width: 30, height: 30, borderRadius: '50%', background: `${t.c}33`,
                      color: t.c, display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 11 }}>
          {t.n.split(' ').map(x => x[0]).join('')}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>{t.n}</div>
          <div style={{ fontSize: 10.5, color: C.textSec }}>{t.r}</div>
        </div>
        <span style={{ width: 7, height: 7, borderRadius: '50%',
                       background: t.s === 'online' ? C.emerald : C.amber }} />
        <span style={{ fontSize: 11, color: C.textSec, minWidth: 14, textAlign: 'right' }}>{t.t}</span>
      </div>
    ))}
  </div>
);

const ActivityFeed = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    {[
      { i: CheckCircle2, c: C.emerald, t: 'Luca M. ha chiuso TKT-2841', ts: '2 min fa' },
      { i: Wrench, c: C.cyan, t: 'Manutenzione "Pulizia filtri HVAC" presa in carico da Marco T.', ts: '14 min fa' },
      { i: AlertTriangle, c: C.terra, t: 'TKT-2847 escalation a URGENTE (SLA -3m)', ts: '16 min fa' },
      { i: Camera, c: C.gold, t: 'Foto aggiunte al TKT-2847 (sifone, ristagno)', ts: '20 min fa' },
      { i: Plus, c: C.cyan, t: 'Marco R. (FO) ha aperto TKT-2847 — Camera 208', ts: '22 min fa' },
    ].map((a, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                            background: 'rgba(255,255,255,0.012)', borderRadius: 8, border: `1px solid ${C.border}` }}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: `${a.c}22`,
                      display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <a.i size={13} color={a.c} />
        </div>
        <span style={{ flex: 1, fontSize: 12.5 }}>{a.t}</span>
        <span style={{ fontSize: 11, color: C.textSec }}>{a.ts}</span>
      </div>
    ))}
  </div>
);

// ============================================================================
// 2. HEATMAP PROBLEMI (pagina dedicata)
// ============================================================================
export function ScreenHeatmap() {
  return (
    <Wrap>
      <PageHeader over="Analytics · Heatmap" title="Mappa problemi"
        sub="Dove si concentrano i guasti — ultimi 90 giorni"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...secondaryBtn }}><Filter size={13} /> Filtri</button>
            <button style={{ ...secondaryBtn }}>Esporta CSV</button>
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
        <Mini label="Ticket totali (90gg)" value="287" sub="-12% vs trim. precedente" accent={C.emerald} />
        <Mini label="Camera più critica" value="208" sub="Galleria · 7 ticket" accent={C.terra} />
        <Mini label="Categoria più frequente" value="Idraulica" sub="34% del totale" accent={C.cyan} />
        <Mini label="MTBF medio camera" value="42 gg" sub="+8 gg vs target" accent={C.gold} />
      </div>

      <Card title="Heatmap globale · 6 hotel × 8 categorie" sub="Più scuro = più ticket">
        <FullHeatmap />
      </Card>
    </Wrap>
  );
}

const FullHeatmap = () => {
  const hotels = ['Scarlatti', 'Arcimboldi', 'Galleria', 'Heart', 'Kira', 'Vivaldi'];
  const cats = ['Idraulica', 'Elettrica', 'HVAC', 'Estetica', 'Ascensori', 'Antincendio', 'Serramenti', 'Altro'];
  const matrix = [
    [.4, .2, .5, .3, .1, .1, .3, .2],
    [.6, .4, .7, .1, .2, .2, .4, .1],
    [.9, .3, .5, .3, .4, .1, .5, .2],
    [.3, .5, .4, .4, .0, .3, .2, .3],
    [.6, .7, .5, .2, .6, .1, .3, .2],
    [.2, .3, .2, .5, .0, .2, .4, .1],
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `120px repeat(${cats.length}, 1fr)`, gap: 5 }}>
      <div />
      {cats.map(c => <div key={c} style={{ ...overline, textAlign: 'center', padding: 6 }}>{c}</div>)}
      {hotels.map((h, i) => (
        <React.Fragment key={h}>
          <div style={{ padding: '12px 6px', fontWeight: 600, fontSize: 12.5, color: C.text }}>{h}</div>
          {matrix[i].map((v, j) => {
            const color = v < 0.25 ? `rgba(34, 211, 238, ${0.05 + v})` :
                         v < 0.6 ? `rgba(59, 130, 246, ${0.15 + v * 0.4})` :
                         `rgba(239, 68, 68, ${0.3 + v * 0.5})`;
            return (
              <div key={j} style={{
                background: color, borderRadius: 6, padding: '16px 6px', textAlign: 'center',
                fontSize: 12.5, fontWeight: 600, color: v > 0.5 ? '#fff' : C.textSec,
                border: `1px solid ${v > 0.7 ? C.terra : C.border}`,
              }}>{Math.round(v * 30)}</div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
};

// ============================================================================
// 3. FORM NUOVO TICKET (modal)
// ============================================================================
export function ScreenNewTicket() {
  return (
    <div style={{ background: C.bg, minHeight: '100%', padding: 36, position: 'relative',
                  fontFamily: 'Inter', color: C.text,
                  backgroundImage: 'radial-gradient(circle at 50% -10%, rgba(59,130,246,0.06), transparent)' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', background: C.surf,
                    borderRadius: 14, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        {/* Header modal */}
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ ...overline }}>Front Office · Galleria</div>
            <div style={{ fontFamily: fontHead, fontSize: 20, fontWeight: 600, marginTop: 2 }}>Nuovo ticket</div>
          </div>
          <button style={{ width: 32, height: 32, borderRadius: 8, background: C.bg,
                          border: `1px solid ${C.border}`, color: C.textSec,
                          display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
            <X size={14} />
          </button>
        </div>

        {/* Regola #0 reminder */}
        <div style={{ margin: '16px 24px', padding: '12px 14px',
                      background: `${C.terra}11`, border: `1px solid ${C.terra}33`,
                      borderRadius: 10, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <Shield size={14} color={C.terra} style={{ marginTop: 2 }} />
          <div style={{ fontSize: 12, color: C.textSec, lineHeight: 1.5 }}>
            <b style={{ color: C.terra }}>Regola #0:</b> verifica prima, scrivi dopo. Niente &quot;il cliente dice&quot;.
            Sali in camera, riproduci il problema, descrivi i fatti che hai visto tu.
          </div>
        </div>

        <div style={{ padding: '8px 24px 24px' }}>
          <Row2>
            <Field label="Camera / Area*" placeholder="Es. 208 · Lobby · Spa" />
            <Field label="Categoria*" placeholder="Idraulica" select />
          </Row2>
          <Field label="Titolo breve*" placeholder="Es. Perdita scarico lavabo" />
          <Field label="Descrizione (cosa hai VERIFICATO)*"
                 placeholder="Cosa - Dove - Da quando - Cosa hai provato. Solo fatti."
                 textarea />

          {/* (rimosso suggeritore AI — non presente nel live attuale) */}

          <Row2>
            <SelectGroup label="Priorità*" opts={['URGENTE', 'ALTA', 'MEDIA', 'BASSA']} active="ALTA" />
            <SelectGroup label="Camera occupata?" opts={['Sì', 'No']} active="No" />
          </Row2>

          <Field label="Foto allegate" placeholder="Trascina qui o clicca per caricare · max 5 · 10MB cad."
                 photo />

          <Row2>
            <Field label="Firma operatore*" placeholder="Marco Rossi" />
            <Field label="Note interne (opzionali)" placeholder="Es. ospite in colazione" />
          </Row2>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24,
                        paddingTop: 18, borderTop: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 11.5, color: C.textSec }}>
              <Shield size={11} style={{ verticalAlign: 'middle' }} /> Tutti i ticket sono firmati con il tuo nome e timestamp
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={secondaryBtn}>Annulla</button>
              <button style={primaryBtn}>Crea ticket <ArrowUpRight size={13} /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row2 = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>{children}</div>
);

const Field = ({ label, placeholder, textarea, photo, select }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: C.textSec, fontWeight: 600, marginBottom: 7 }}>{label}</div>
    {textarea ? (
      <textarea placeholder={placeholder} rows={4} style={{
        width: '100%', padding: '12px 14px', background: C.bg,
        border: `1px solid ${C.border}`, borderRadius: 9,
        color: C.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical',
      }} />
    ) : photo ? (
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{
          aspectRatio: '1.4', width: 80, borderRadius: 9, background: `${C.cyan}22`,
          display: 'grid', placeItems: 'center', border: `1px solid ${C.border}`, fontSize: 10, color: C.textSec,
        }}>📷</div>
        <div style={{
          flex: 1, padding: '12px 14px', background: 'transparent',
          border: `1px dashed ${C.border}`, borderRadius: 9,
          color: C.textSec, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Camera size={14} /> {placeholder}
        </div>
      </div>
    ) : (
      <div style={{ position: 'relative' }}>
        <input placeholder={placeholder} style={{
          width: '100%', padding: '12px 14px', background: C.bg,
          border: `1px solid ${C.border}`, borderRadius: 9,
          color: C.text, fontSize: 13, fontFamily: 'inherit', outline: 'none',
          paddingRight: select ? 40 : 14,
        }} />
        {select && <ChevronDown size={14} style={{ position: 'absolute', right: 12, top: 14, color: C.textSec }} />}
      </div>
    )}
  </div>
);

const SelectGroup = ({ label, opts, active }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: C.textSec, fontWeight: 600, marginBottom: 7 }}>{label}</div>
    <div style={{ display: 'flex', gap: 4 }}>
      {opts.map(o => (
        <button key={o} style={{
          flex: 1, padding: '10px 6px', borderRadius: 8, fontSize: 11.5,
          background: o === active ? `${C.gold}22` : C.bg,
          color: o === active ? C.gold : C.textSec,
          border: `1px solid ${o === active ? C.gold + '55' : C.border}`,
          fontWeight: o === active ? 600 : 400, cursor: 'pointer',
        }}>{o}</button>
      ))}
    </div>
  </div>
);

// ============================================================================
// 4. TECNICO — I Miei Lavori
// ============================================================================
export function ScreenTechnician() {
  return (
    <Wrap>
      <PageHeader over="Maintenance · Tecnico" title="Ciao Luca · I tuoi lavori"
        sub="3 ticket attivi · 2 manutenzioni programmate questa settimana"
        action={<button style={secondaryBtn}><Clock size={13} /> Turno: 08-17</button>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 18 }}>
        <Mini label="Da fare oggi" value="5" sub="2 urgenti" accent={C.terra} />
        <Mini label="Completati oggi" value="3" sub="Tempo medio 1h 12m" accent={C.emerald} />
        <Mini label="Settimana" value="22" sub="Ticket + 4 manut." accent={C.cyan} />
      </div>

      <Card title="Coda lavori · ordine consigliato" sub="Ordinati per priorità + distanza camera"
            action={<button style={{ ...secondaryBtn, padding: '7px 12px' }}><Filter size={12} /> Filtra</button>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { id: 'TKT-2847', pr: 'URGENTE', prC: C.terra, room: 'Cam 208', cat: 'Idraulica',
              title: 'Perdita continua scarico lavabo', sla: '-3m', state: 'da-prendere' },
            { id: 'TKT-2842', pr: 'MEDIA', prC: '#FBBF24', room: 'Cam 109', cat: 'Idraulica',
              title: 'Rubinetto bagno gocciola', state: 'in-corso' },
            { id: 'TKT-2839', pr: 'BASSA', prC: '#64748B', room: 'Cam 312', cat: 'Idraulica',
              title: 'Doccia: getto irregolare', state: 'da-prendere' },
            { id: 'MNT-128', pr: 'PROGR.', prC: C.gold, room: 'Tutti', cat: 'Sanific. ascensori',
              title: 'Sanificazione ascensori cabina A+B', state: 'schedulata', date: 'Dom 12 Giu' },
          ].map((j, i) => (
            <div key={j.id} style={{
              display: 'grid', gridTemplateColumns: '4px 70px 110px 1fr auto auto', gap: 14, alignItems: 'center',
              padding: '14px 14px 14px 0', borderRadius: 11,
              background: i === 0 ? `${C.terra}08` : 'rgba(255,255,255,0.015)',
              border: `1px solid ${i === 0 ? C.terra + '33' : C.border}`,
            }}>
              <div style={{ background: j.prC, height: 36, borderRadius: 12,
                            boxShadow: i === 0 ? `0 0 8px ${j.prC}` : 'none' }} />
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.textSec }}>{j.id}</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                            padding: '4px 9px', borderRadius: 5, width: 'fit-content',
                            background: `${j.prC}22`, color: j.prC }}>{j.pr}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {j.room} <span style={{ color: C.textSec, fontWeight: 400 }}>· {j.cat}</span>
                </div>
                <div style={{ fontSize: 12, color: C.textSec, marginTop: 3 }}>{j.title}</div>
              </div>
              {j.sla && <span style={{ fontSize: 10, color: C.terra, fontWeight: 700,
                                       padding: '3px 8px', background: `${C.terra}22`, borderRadius: 4 }}>
                SLA {j.sla}
              </span>}
              {j.date && <span style={{ fontSize: 11, color: C.textSec }}>{j.date}</span>}
              <button style={{
                ...(i === 0 ? primaryBtn : { ...secondaryBtn, color: C.gold, borderColor: `${C.gold}55` }),
                fontSize: 12, padding: '8px 14px',
              }}>{j.state === 'in-corso' ? 'Continua' : j.state === 'schedulata' ? 'Apri' : 'Prendi in carico'}</button>
            </div>
          ))}
        </div>
      </Card>
    </Wrap>
  );
}

// ============================================================================
// 5. HOUSEKEEPING — Segnalazione rapida
// ============================================================================
export function ScreenHousekeeping() {
  const cats = [
    { i: '💧', n: 'Idraulica', c: C.cyan },
    { i: '⚡', n: 'Elettrica', c: C.amber },
    { i: '🌡️', n: 'Climatizzazione', c: C.gold },
    { i: '📺', n: 'Disp. camera', c: '#A78BFA' },
    { i: '🚪', n: 'Serramenti', c: '#F472B6' },
    { i: '🎨', n: 'Pitture', c: C.emerald },
    { i: '🛁', n: 'Siliconature', c: C.cyan },
    { i: '❓', n: 'Altro', c: C.textSec },
  ];
  return (
    <Wrap>
      <PageHeader over="Housekeeping · Galleria" title="Segnalazione rapida"
        sub="Apri ticket in 10 secondi · niente filtri, niente fronzoli"
      />

      <Card title="Quale problema hai trovato?" sub="Tocca la categoria">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {cats.map(c => (
            <button key={c.n} style={{
              background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12,
              padding: '20px 16px', cursor: 'pointer', textAlign: 'left',
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              <div style={{ fontSize: 28 }}>{c.i}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: c.c }}>{c.n}</div>
            </button>
          ))}
        </div>
      </Card>

      <div style={{ height: 16 }} />

      <Card title="Le mie segnalazioni recenti" sub="Ultime 7 · oggi">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            { st: 'closed', n: 'Cam 412 · Pittura sbeccata', t: 'Chiuso · 09:14' },
            { st: 'inprogress', n: 'Cam 318 · TV non risponde', t: 'In lavoro · 10:22' },
            { st: 'open', n: 'Cam 205 · Maniglia armadio rotta', t: 'Aperto · 11:08' },
            { st: 'open', n: 'Spa · Siliconatura piscina', t: 'Aperto · 11:30' },
          ].map((h, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                                  background: 'rgba(255,255,255,0.012)', borderRadius: 9, border: `1px solid ${C.border}` }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%',
                             background: h.st === 'closed' ? C.emerald : h.st === 'inprogress' ? C.gold : C.terra }} />
              <span style={{ flex: 1, fontSize: 13 }}>{h.n}</span>
              <span style={{ fontSize: 11, color: C.textSec }}>{h.t}</span>
            </div>
          ))}
        </div>
      </Card>
    </Wrap>
  );
}

// ============================================================================
// 6. UTENTI & RUOLI
// ============================================================================
export function ScreenUsers() {
  return (
    <Wrap>
      <PageHeader over="Sistema · Utenti" title="Utenti & Ruoli"
        sub="34 utenti attivi · 6 ruoli · 6 hotel"
        action={<button style={primaryBtn}><Plus size={13} /> Invita utente</button>}
      />

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px',
                      background: C.surf, borderRadius: 9, border: `1px solid ${C.border}`, minWidth: 320 }}>
          <Search size={13} color={C.textSec} />
          <span style={{ color: C.textSec, fontSize: 13 }}>Cerca utente, email...</span>
        </div>
        {['Super Admin · 2', 'Hotel Manager · 6', 'Capo Manutentore · 6', 'Tecnico · 12', 'Front Office · 6', 'Housekeeping · 2'].map((p, i) => (
          <button key={p} style={{
            padding: '8px 14px', borderRadius: 999, fontSize: 12,
            background: i === 3 ? `${C.gold}22` : C.surf,
            color: i === 3 ? C.gold : C.textSec,
            border: `1px solid ${i === 3 ? C.gold + '55' : C.border}`,
            fontWeight: i === 3 ? 600 : 400,
          }}>{p}</button>
        ))}
      </div>

      <Card>
        <div style={{ display: 'grid',
                      gridTemplateColumns: '32px 1.5fr 1fr 1fr 1fr 1fr 80px',
                      gap: 14, padding: '12px 18px', borderBottom: `1px solid ${C.border}`,
                      ...overline, alignItems: 'center' }}>
          <span></span>
          <span>Nome & Email</span>
          <span>Ruolo</span>
          <span>Hotel assegnati</span>
          <span>Ultimo accesso</span>
          <span>Stato</span>
          <span></span>
        </div>
        {[
          { n: 'Marco Rossi', e: 'm.rossi@investhosp.com', r: 'Super Admin', h: 'Tutti (6)', la: '2 min fa', s: 'active', c: C.gold, ini: 'MR' },
          { n: 'Sara Konti', e: 's.konti@investhosp.com', r: 'Capo Manutentore', h: 'Galleria · Arcimboldi', la: '14 min fa', s: 'active', c: C.cyan, ini: 'SK' },
          { n: 'Luca Marini', e: 'l.marini@investhosp.com', r: 'Tecnico', h: 'Galleria', la: '5 min fa', s: 'active', c: C.emerald, ini: 'LM' },
          { n: 'Anna Verde', e: 'a.verde@investhosp.com', r: 'Front Office', h: 'Arcimboldi', la: '42 min fa', s: 'active', c: C.amber, ini: 'AV' },
          { n: 'Diego Bo', e: 'd.bo@investhosp.com', r: 'Hotel Manager', h: 'Scarlatti', la: '3 ore fa', s: 'active', c: C.gold, ini: 'DB' },
          { n: 'Test User', e: 'test@investhosp.com', r: 'Housekeeping', h: 'Heart', la: 'Mai', s: 'pending', c: C.textDim, ini: 'TU' },
        ].map((u, i) => (
          <div key={u.e} style={{ display: 'grid',
                                  gridTemplateColumns: '32px 1.5fr 1fr 1fr 1fr 1fr 80px',
                                  gap: 14, padding: '14px 18px',
                                  borderBottom: i < 5 ? `1px solid ${C.border}` : 'none',
                                  alignItems: 'center', cursor: 'pointer' }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: `${u.c}33`,
                          color: u.c, display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 11 }}>
              {u.ini}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{u.n}</div>
              <div style={{ fontSize: 11, color: C.textSec, marginTop: 2 }}>{u.e}</div>
            </div>
            <span style={{ fontSize: 12 }}>{u.r}</span>
            <span style={{ fontSize: 12, color: C.textSec }}>{u.h}</span>
            <span style={{ fontSize: 12, color: C.textSec }}>{u.la}</span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, width: 'fit-content',
              background: u.s === 'active' ? `${C.emerald}22` : `${C.amber}22`,
              color: u.s === 'active' ? C.emerald : C.amber,
            }}>{u.s === 'active' ? 'ATTIVO' : 'PENDING'}</span>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button style={iconBtn}><Edit3 size={12} /></button>
              <button style={iconBtn}><MoreHorizontal size={12} /></button>
            </div>
          </div>
        ))}
      </Card>
    </Wrap>
  );
}

const iconBtn = {
  width: 28, height: 28, borderRadius: 6, background: C.bg,
  border: `1px solid ${C.border}`, color: C.textSec,
  display: 'grid', placeItems: 'center', cursor: 'pointer',
};

// ============================================================================
// 7. HOTEL DEL GRUPPO
// ============================================================================
export function ScreenHotels() {
  const hotels = [
    { n: 'Hotel Scarlatti', c: 'Roma', r: 86, t: 4, eff: 87.2, ae: false, color: C.gold },
    { n: 'Hotel Arcimboldi', c: 'Milano', r: 124, t: 9, eff: 84.1, ae: true, color: C.cyan },
    { n: 'Hotel Galleria', c: 'Milano', r: 96, t: 12, eff: 81.5, ae: false, color: C.emerald },
    { n: 'Hotel Heart', c: 'Firenze', r: 64, t: 5, eff: 77.3, ae: false, color: C.amber },
    { n: 'Hotel Kira', c: 'Roma', r: 91, t: 8, eff: 76.8, ae: true, color: '#A78BFA' },
    { n: 'Hotel Vivaldi', c: 'Venezia', r: 52, t: 2, eff: 71.6, ae: false, color: '#F472B6' },
  ];
  return (
    <Wrap>
      <PageHeader over="Group · 6 properties" title="Hotel del Gruppo"
        action={<button style={primaryBtn}><Plus size={13} /> Aggiungi hotel</button>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {hotels.map(h => (
          <div key={h.n} style={{
            background: C.surf, borderRadius: 14, border: `1px solid ${C.border}`,
            overflow: 'hidden', cursor: 'pointer',
          }}>
            <div style={{
              height: 96, background: `linear-gradient(135deg, ${h.color}22, ${h.color}05)`,
              borderBottom: `1px solid ${C.border}`, position: 'relative',
              display: 'flex', alignItems: 'flex-end', padding: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10, background: h.color,
                  display: 'grid', placeItems: 'center',
                }}><Building2 size={18} color="#fff" /></div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, fontFamily: fontHead }}>{h.n}</div>
                  <div style={{ fontSize: 11.5, color: C.textSec, marginTop: 2,
                                display: 'flex', alignItems: 'center', gap: 4 }}>
                    <MapPin size={10} /> {h.c}
                  </div>
                </div>
              </div>
              {h.ae && <span style={{
                position: 'absolute', top: 12, right: 12,
                fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
                padding: '3px 7px', borderRadius: 4,
                background: `${C.gold}33`, color: C.gold, border: `1px solid ${C.gold}55`,
                display: 'inline-flex', alignItems: 'center', gap: 3,
              }}><Zap size={9} /> ALL-ELECTRIC</span>}
            </div>
            <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              <Stat3 label="Camere" value={h.r} />
              <Stat3 label="Ticket aperti" value={h.t} accent={h.t > 8 ? C.terra : C.text} />
              <Stat3 label="Eff." value={h.eff} accent={C.emerald} />
            </div>
          </div>
        ))}
      </div>
    </Wrap>
  );
}

const Stat3 = ({ label, value, accent }) => (
  <div>
    <div style={{ ...overline, marginBottom: 4 }}>{label}</div>
    <div style={{ fontFamily: fontHead, fontSize: 22, fontWeight: 600, color: accent || C.text,
                  letterSpacing: '-0.03em', lineHeight: 1 }}>{value}</div>
  </div>
);

// ============================================================================
// 8. BACHECA
// ============================================================================
export function ScreenBacheca() {
  return (
    <Wrap>
      <PageHeader over="Internal · Bacheca" title="Bacheca team"
        sub="Comunicazioni, regole, novità per tutto il team"
        action={<button style={primaryBtn}><Plus size={13} /> Nuovo post</button>}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {[
          { t: 'pinned', i: '📌', title: 'Nuove categorie Housekeeping attive',
            body: 'Aggiunte 4 categorie: Dispositivi camera, Siliconature, Pitture, Serramenti. Trovate i dettagli nella Guida Categorie HK in Documenti.',
            au: 'Marco R. (Super Admin)', t2: '2 giorni fa', tag: 'IMPORTANTE', tagC: C.gold },
          { i: '🆕', title: 'Aggiornamento HostFix v3.0',
            body: 'Manutenzione ascensori semestrale resa automatica. Dashboard energia ridisegnata. Vedere changelog completo.',
            au: 'PTK Solutions', t2: '1 settimana fa', tag: 'NOVITÀ', tagC: C.cyan },
          { i: '⚠️', title: 'Mai aprire ticket basati su "il cliente dice"',
            body: 'Reminder importante: prima di aprire un ticket, verificate sempre voi stessi il problema. Vedi Guida Front Office sezione Regola #0.',
            au: 'Marco R.', t2: '2 settimane fa', tag: 'POLICY', tagC: C.terra },
          { i: '🎉', title: 'Hotel Scarlatti raggiunge indice 87.2',
            body: 'Complimenti al team Scarlatti per la migliore performance energetica del trimestre. Best practice condivise nella sezione Documenti.',
            au: 'Marco R.', t2: '3 settimane fa', tag: 'KUDOS', tagC: C.emerald },
        ].map((p, i) => (
          <div key={i} style={{
            background: C.surf, borderRadius: 12, padding: 20,
            border: `1px solid ${p.t === 'pinned' ? C.gold + '44' : C.border}`,
            position: 'relative',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 10, background: `${p.tagC}22`,
                display: 'grid', placeItems: 'center', fontSize: 20, flexShrink: 0,
              }}>{p.i}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontFamily: fontHead, fontSize: 16, fontWeight: 600 }}>{p.title}</span>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
                                padding: '3px 7px', borderRadius: 4,
                                background: `${p.tagC}22`, color: p.tagC }}>{p.tag}</span>
                </div>
                <p style={{ fontSize: 13, color: C.text, opacity: 0.85, lineHeight: 1.5, margin: 0 }}>{p.body}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12,
                              fontSize: 11.5, color: C.textSec }}>
                  <span>{p.au}</span><span>· {p.t2}</span>
                  <button style={{ background: 'transparent', border: 'none', color: C.gold, fontSize: 12, cursor: 'pointer' }}>
                    Vedi dettagli →
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Wrap>
  );
}

// ============================================================================
// 9. ASCENSORI
// ============================================================================
export function ScreenElevators() {
  return (
    <Wrap>
      <PageHeader over="Critical · Ascensori" title="Stato ascensori live"
        sub="11 ascensori monitorati · 1 in manutenzione · 2 con alert"
        action={<button style={secondaryBtn}><Bell size={13} /> Notifiche</button>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 18 }}>
        {[
          { h: 'Galleria · Cabina A', s: 'OK', stC: C.emerald, last: '34 sec fa', uptime: '99.8%', issues: 0 },
          { h: 'Galleria · Cabina B', s: 'OK', stC: C.emerald, last: '12 sec fa', uptime: '99.9%', issues: 0 },
          { h: 'Arcimboldi · Cabina A', s: 'MANUTENZIONE', stC: C.amber, last: '2 min fa', uptime: '98.1%', issues: 1 },
          { h: 'Arcimboldi · Cabina B', s: 'ALERT', stC: C.terra, last: '40 sec fa', uptime: '94.5%', issues: 3 },
          { h: 'Scarlatti · Cabina A', s: 'OK', stC: C.emerald, last: '8 sec fa', uptime: '99.6%', issues: 0 },
          { h: 'Kira · Cabina B', s: 'ALERT', stC: C.terra, last: '1 min fa', uptime: '96.2%', issues: 2 },
        ].map(e => (
          <div key={e.h} style={{
            background: C.surf, borderRadius: 14, padding: 20,
            border: `1px solid ${e.s !== 'OK' ? e.stC + '55' : C.border}`,
            position: 'relative', overflow: 'hidden',
          }}>
            {e.s !== 'OK' && <div style={{
              position: 'absolute', top: 0, right: 0, width: 100, height: 100,
              background: `radial-gradient(circle at top right, ${e.stC}22, transparent 60%)`,
            }} />}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertOctagon size={16} color={e.stC} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>{e.h}</span>
              </div>
              <span style={{
                fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em',
                padding: '3px 8px', borderRadius: 4,
                background: `${e.stC}22`, color: e.stC,
              }}>{e.s}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 14 }}>
              <Stat3 label="Last ping" value={e.last} />
              <Stat3 label="Uptime 30g" value={e.uptime} accent={parseFloat(e.uptime) > 99 ? C.emerald : C.amber} />
              <Stat3 label="Issues 7g" value={e.issues} accent={e.issues > 0 ? C.terra : C.emerald} />
            </div>
          </div>
        ))}
      </div>
    </Wrap>
  );
}

// ============================================================================
// 10. CONTRATTI DI MANUTENZIONE
// ============================================================================
export function ScreenContracts() {
  return (
    <Wrap>
      <PageHeader over="Procurement · Contratti" title="Contratti di manutenzione"
        sub="14 contratti attivi · 2 in scadenza nei prossimi 60 giorni"
        action={<button style={primaryBtn}><Plus size={13} /> Nuovo contratto</button>}
      />

      <Card title="Calendario rinnovi" sub="Prossimi 12 mesi">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { exp: 22, days: 22, name: 'Schindler · Manutenzione ascensori', cat: 'Ascensori', cost: '24.500 €/anno', auto: false, urg: true },
            { exp: 38, days: 38, name: 'Idraulica Rossi SRL', cat: 'Idraulica', cost: '8.400 €/anno', auto: true, urg: false },
            { exp: 92, days: 92, name: 'Caldaie Beretta · Service plan', cat: 'Termoidraulica', cost: '12.000 €/anno', auto: true },
            { exp: 145, days: 145, name: 'Antincendio Securpro', cat: 'Sicurezza', cost: '6.200 €/anno', auto: true },
            { exp: 280, days: 280, name: 'Cleantech · Sanificazione HVAC', cat: 'HVAC', cost: '18.300 €/anno', auto: false },
          ].map((c, i) => (
            <div key={i} style={{ display: 'grid',
                                  gridTemplateColumns: '60px 1fr 130px 140px 90px 80px',
                                  gap: 14, padding: '14px 16px', borderRadius: 11,
                                  background: c.urg ? `${C.terra}11` : 'rgba(255,255,255,0.012)',
                                  border: `1px solid ${c.urg ? C.terra + '44' : C.border}`, alignItems: 'center' }}>
              <div style={{ textAlign: 'center', padding: '6px 0', borderRadius: 8,
                            background: c.urg ? C.terra : C.bg, color: c.urg ? '#fff' : C.text }}>
                <div style={{ fontFamily: fontHead, fontSize: 18, fontWeight: 700, lineHeight: 1 }}>{c.days}</div>
                <div style={{ fontSize: 9, marginTop: 2, opacity: 0.85, letterSpacing: '0.1em' }}>GG</div>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 11, color: C.textSec, marginTop: 2 }}>{c.cat}</div>
              </div>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: C.gold }}>{c.cost}</span>
              <span style={{ fontSize: 11.5, color: C.textSec }}>
                {c.auto ? '🔄 Rinnovo automatico' : '✋ Manuale'}
              </span>
              <span style={{ fontSize: 9.5, fontWeight: 700, padding: '3px 7px', borderRadius: 4,
                            background: c.urg ? `${C.terra}22` : `${C.emerald}22`,
                            color: c.urg ? C.terra : C.emerald, textAlign: 'center' }}>
                {c.urg ? 'IN SCAD.' : 'ATTIVO'}
              </span>
              <button style={{ ...secondaryBtn, padding: '7px 10px', fontSize: 11.5 }}>
                Apri PDF
              </button>
            </div>
          ))}
        </div>
      </Card>
    </Wrap>
  );
}

// ============================================================================
// 11. ANALYTICS
// ============================================================================
export function ScreenAnalytics() {
  return (
    <Wrap>
      <PageHeader over="Insights · Group analytics" title="Analytics & KPI"
        sub="Operations performance · ultimi 90 giorni"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={secondaryBtn}>90 giorni</button>
            <button style={{ ...secondaryBtn, color: C.gold, borderColor: `${C.gold}55` }}>Esporta PDF</button>
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
        <Mini label="Ticket totali" value="287" sub="-12% vs trim. prec." accent={C.emerald} />
        <Mini label="MTTR medio" value="2h 14m" sub="-18 min · ottimo" accent={C.emerald} />
        <Mini label="SLA rispettato" value="94.2%" sub="Target 92%" accent={C.gold} />
        <Mini label="Costo medio ticket" value="€ 47" sub="-9% vs trim." accent={C.cyan} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14, marginBottom: 18 }}>
        <Card title="Ticket per categoria" sub="Distribuzione 90 giorni">
          <CatBars />
        </Card>
        <Card title="Performance tecnici" sub="Top 5 · ticket chiusi">
          <TechRank />
        </Card>
      </div>

      <Card title="Trend ticket nel tempo" sub="Per priorità · 12 mesi">
        <BigTrend />
      </Card>
    </Wrap>
  );
}

const CatBars = () => {
  const data = [
    { c: 'Idraulica', n: 97, p: 100 },
    { c: 'Elettrica', n: 58, p: 60 },
    { c: 'HVAC', n: 47, p: 48 },
    { c: 'Estetica', n: 32, p: 33 },
    { c: 'Ascensori', n: 24, p: 25 },
    { c: 'Elettrodom.', n: 18, p: 19 },
    { c: 'Altro', n: 11, p: 11 },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      {data.map(d => (
        <div key={d.c} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 40px', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: C.text }}>{d.c}</span>
          <div style={{ height: 7, background: C.bg, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${d.p}%`, height: '100%', background: C.gold }} />
          </div>
          <span style={{ fontSize: 12, color: C.textSec, textAlign: 'right' }}>{d.n}</span>
        </div>
      ))}
    </div>
  );
};

const TechRank = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
    {[
      { n: 'Sara Konti', c: 47, c2: C.emerald, ini: 'SK' },
      { n: 'Luca Marini', c: 38, c2: C.cyan, ini: 'LM' },
      { n: 'Marco Trento', c: 32, c2: C.amber, ini: 'MT' },
      { n: 'Diego Bo', c: 28, c2: C.gold, ini: 'DB' },
      { n: 'Anna Verde', c: 21, c2: '#A78BFA', ini: 'AV' },
    ].map((t, i) => (
      <div key={t.n} style={{ display: 'grid', gridTemplateColumns: '24px 30px 1fr 40px', gap: 10, alignItems: 'center',
                              padding: '8px 10px', background: 'rgba(255,255,255,0.012)',
                              borderRadius: 8, border: `1px solid ${C.border}` }}>
        <span style={{ fontFamily: fontHead, fontSize: 13, fontWeight: 600,
                       color: i === 0 ? C.gold : C.textSec, textAlign: 'center' }}>#{i + 1}</span>
        <div style={{ width: 26, height: 26, borderRadius: '50%', background: `${t.c2}33`,
                      color: t.c2, display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 10 }}>
          {t.ini}
        </div>
        <span style={{ fontSize: 12.5, fontWeight: 500 }}>{t.n}</span>
        <span style={{ fontFamily: fontHead, fontWeight: 600, fontSize: 14, color: C.text, textAlign: 'right' }}>{t.c}</span>
      </div>
    ))}
  </div>
);

const BigTrend = () => {
  const W = 880, H = 180, PAD = 30;
  const months = ['Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic', 'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu'];
  const dataU = [4, 5, 6, 8, 7, 5, 4, 6, 5, 3, 4, 3];
  const dataH = [12, 14, 11, 13, 14, 12, 16, 15, 13, 14, 13, 12];
  const dataM = [18, 22, 19, 24, 21, 18, 25, 23, 20, 22, 19, 24];
  const max = 30;
  const toPath = (data) => data.map((v, i) => {
    const x = PAD + (i / (data.length - 1)) * (W - 2 * PAD);
    const y = PAD + (1 - v / max) * (H - 2 * PAD);
    return `${i === 0 ? 'M' : 'L'}${x},${y}`;
  }).join(' ');
  return (
    <div>
      <div style={{ display: 'flex', gap: 14, marginBottom: 12, fontSize: 11, color: C.textSec }}>
        <Legend2 c={C.terra} l="URGENTE" />
        <Legend2 c={C.amber} l="ALTA" />
        <Legend2 c={C.gold} l="MEDIA + BASSA" />
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }}>
        {[0, 1, 2, 3, 4].map(i => (
          <line key={i} x1={PAD} x2={W - PAD}
                y1={PAD + i * (H - 2 * PAD) / 4} y2={PAD + i * (H - 2 * PAD) / 4}
                stroke={C.border} strokeDasharray="2 3" />
        ))}
        <path d={toPath(dataM)} stroke={C.gold} strokeWidth="2" fill="none" />
        <path d={toPath(dataH)} stroke={C.amber} strokeWidth="2" fill="none" />
        <path d={toPath(dataU)} stroke={C.terra} strokeWidth="2" fill="none" />
        {months.map((m, i) => (
          <text key={m} x={PAD + (i / (months.length - 1)) * (W - 2 * PAD)}
                y={H - 8} fill={C.textSec} fontSize="10" textAnchor="middle">{m}</text>
        ))}
      </svg>
    </div>
  );
};

const Legend2 = ({ c, l }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
    <span style={{ width: 18, height: 2, background: c }} /> {l}
  </span>
);

// ============================================================================
// 12. PROFILO & IMPOSTAZIONI
// ============================================================================
export function ScreenProfile() {
  return (
    <Wrap>
      <PageHeader over="Account" title="Profilo & Impostazioni" />

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20 }}>
        {/* Side menu */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {[
            { i: '👤', n: 'Profilo', a: true },
            { i: '🔔', n: 'Notifiche' },
            { i: '🌓', n: 'Aspetto · tema' },
            { i: '🔐', n: 'Sicurezza & 2FA' },
            { i: '🗂️', n: 'Sessioni attive' },
            { i: '📥', n: 'Esportazioni' },
            { i: '🏨', n: 'Hotel preferiti' },
            { i: '🚪', n: 'Logout', danger: true },
          ].map(m => (
            <button key={m.n} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderRadius: 9, fontSize: 13, cursor: 'pointer', textAlign: 'left',
              background: m.a ? `${C.gold}15` : 'transparent',
              color: m.a ? C.gold : (m.danger ? '#FCA5A5' : C.text),
              border: m.a ? `1px solid ${C.gold}33` : '1px solid transparent',
              fontWeight: m.a ? 600 : 400,
            }}>
              <span style={{ width: 22, textAlign: 'center' }}>{m.i}</span>
              {m.n}
            </button>
          ))}
        </div>

        {/* Profilo content */}
        <Card title="Informazioni personali" sub="Visibili al team del tuo gruppo">
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 24,
                        padding: '16px 0', borderBottom: `1px solid ${C.border}` }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: `linear-gradient(135deg, ${C.gold}, ${C.cyan})`,
              display: 'grid', placeItems: 'center', fontSize: 24, fontWeight: 600, color: '#fff',
            }}>MR</div>
            <div>
              <div style={{ fontFamily: fontHead, fontSize: 18, fontWeight: 600 }}>Marco Rossi</div>
              <div style={{ fontSize: 12.5, color: C.textSec, marginTop: 4 }}>
                <Crown size={11} style={{ verticalAlign: 'middle', color: C.gold }} /> Super Admin · Invest Hospitality
              </div>
            </div>
            <button style={{ ...secondaryBtn, marginLeft: 'auto', padding: '8px 14px' }}>
              Cambia foto
            </button>
          </div>

          <Row2>
            <Field label="Nome" placeholder="Marco" />
            <Field label="Cognome" placeholder="Rossi" />
          </Row2>
          <Field label="Email aziendale" placeholder="m.rossi@investhosp.com" />
          <Field label="Telefono" placeholder="+39 333 555 1234" />
          <Row2>
            <Field label="Ruolo" placeholder="Super Admin" select />
            <Field label="Lingua interfaccia" placeholder="Italiano" select />
          </Row2>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button style={secondaryBtn}>Annulla</button>
            <button style={primaryBtn}>Salva modifiche</button>
          </div>
        </Card>
      </div>
    </Wrap>
  );
}
